import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { WEEKDAY, Product } from '../../../models';
import { LibCoreLayoutModule } from '../../core-layout';
import { PipesModule } from '../../../pipes';
import { EnumUtils } from '../../../utils';
import { CommonModule } from '@angular/common';

export interface ProductHappyHourDialogComponentData {
  product: Product;
  currency: string;
}

@Component({
  selector: 'lib-product-happy-hour-dialog',
  templateUrl: './product-happy-hour-dialog.component.html',
  styleUrls: ['./product-happy-hour-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    PipesModule,
    TranslateModule,
    // material design
    MatButtonModule,
  ],
})
export class ProductHappyHourDialogComponent {
  public product: Product;
  public discountText: string;
  public happyHourWeekdays: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: ProductHappyHourDialogComponentData,
    private dialogRef: MatDialogRef<ProductHappyHourDialogComponent>,
    private translateService: TranslateService
  ) {
    this.product = this.data?.product;

    this.discountText = `- ${this.product?.happyHours?.amount} ${
      this.product?.happyHours?.percentage ? '%' : data?.currency
    }`;

    this.happyHourWeekdays = this.getSmartWeekdays(
      this.product?.happyHours?.weekDays
    );
  }

  public getTimeToDisplay(timeAsStr: string): string {
    return timeAsStr.substring(0, 5);
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  /**
   * Convert array of weekdays to string
   * e.g. [Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday] -> 'Monday - Sunday'
   * e.g. [Monday, Tuesday, Wednesday, Friday, Saturday] -> 'Monday - Wednesday, Friday, Saturday'
   * */
  private getSmartWeekdays(weekdays: WEEKDAY[]): string {
    if (!weekdays?.length) {
      return '';
    }

    const sortedWeekdays = this.sortWeekdays(weekdays);
    const weekdayAsStr = EnumUtils.convertEnumToArray(WEEKDAY);

    let smartWeekdayStr = '';

    for (let i = 0; i < sortedWeekdays.length; ) {
      if (smartWeekdayStr.length) {
        smartWeekdayStr += ', ';
      }

      smartWeekdayStr += this.translateService.instant(
        'pipes.weekday_short.' + sortedWeekdays[i]
      );

      if (i === sortedWeekdays.length - 1) {
        break;
      }

      let j = i + 1;
      for (; j < sortedWeekdays.length; j += 1) {
        const nextWeekdayAsNr = weekdayAsStr.indexOf(sortedWeekdays[j]);
        const previousWeekdayAsNr = weekdayAsStr.indexOf(sortedWeekdays[j - 1]);

        if (previousWeekdayAsNr + 1 !== nextWeekdayAsNr) {
          break;
        }
      }

      j -= 1;

      if (j > i + 1) {
        smartWeekdayStr +=
          ' - ' +
          this.translateService.instant(
            'pipes.weekday_short.' + sortedWeekdays[j]
          );
        i = j + 1;
      } else {
        i += 1;
      }
    }

    return smartWeekdayStr;
  }

  private sortWeekdays(weekdays: WEEKDAY[]): WEEKDAY[] {
    const weekdayAsStr = EnumUtils.convertEnumToArray(WEEKDAY);
    const weekdayAsNr = weekdays
      .map((w) => weekdayAsStr.indexOf(w))
      .filter((w) => w >= 0);

    weekdayAsNr.sort();
    return weekdayAsNr.map((w) => weekdayAsStr[w] as WEEKDAY);
  }
}
